import type { LibrarySymbolInfo, ResolutionString, SubscribeBarsCallback, Bar } from 'charting-library'
import { exchangeSocketService, useKlineStore } from 'store'
import { SocketEventName } from 'store/src/socket/SocketService'
import { useChangeResolution } from 'store/src/hooks'

type SubscriberUID = string

type Handler = {
  id: SubscriberUID
  callback: SubscribeBarsCallback
}

type SubscriptionItem = {
  handlers: Handler[]
  subscriberUID: SubscriberUID
  resolution: ResolutionString
}

export const useTradingViewStreaming = () => {
  const channelToSubscription = new Map<string, SubscriptionItem>()
  const lastHistoryKline = ref<Bar>()
  const klineStore = useKlineStore()

  /** 监听K线回调 */
  const listenKlineCallback = (res: SocketResponse.KlineResponse) => {
    if (res.params.r !== klineStore.currentResolution.toString()) {
      return
    }
    const subscriptionItem = channelToSubscription.get(res.params.p)
    if (!subscriptionItem) {
      return
    }

    const bar = klineStore.formatSocketKline(res.data)
    if (!lastHistoryKline.value) {
      return
    }
    if (bar.time < lastHistoryKline.value.time) {
      return
    }
    lastHistoryKline.value = bar
    subscriptionItem.handlers.forEach((handler) => {
      handler.callback(bar)
    })
  }

  const listenKline = () => {
    exchangeSocketService.onMessage<SocketResponse.KlineResponse>(SocketEventName.Kline, listenKlineCallback)
  }

  const setLastHistoryKline = (data: Bar) => {
    lastHistoryKline.value = data
  }

  onMounted(() => {
    listenKline()
  })

  const subscribeBars = (symbolInfo: LibrarySymbolInfo, resolution: ResolutionString, onTick: SubscribeBarsCallback, subscriberUID: string) => {
    const handler = {
      id: subscriberUID,
      callback: onTick,
    }

    const subscriptionItem = {
      subscriberUID,
      resolution,
      handlers: [handler],
    }

    channelToSubscription.set(symbolInfo.name, subscriptionItem)

    klineStore.subscribeKline()
  }

  const unsubscribeBars = (_subscriberUID: string) => {
    for (const channelString of channelToSubscription.keys()) {
      const subscriptionItem = channelToSubscription.get(channelString)!
      const handlerIndex = subscriptionItem!.handlers.findIndex((handler) => handler.id === _subscriberUID)
      if (handlerIndex !== -1) {
        const resolution = subscriptionItem.resolution
        if (resolution) {
          klineStore.unsubscribeKlineWithResolution(resolution)
        }
      }
    }
  }

  useChangeResolution((_, oldResolution) => {
    klineStore.unsubscribeKlineWithResolution(oldResolution)
  })

  return {
    channelToSubscription,
    subscribeBars,
    setLastHistoryKline,
    unsubscribeBars,
  }
}
